import { useCallback, useEffect, useState } from "react"

const WORKER_URL = (import.meta.env.VITE_WORKER_URL ?? "").replace(/\/$/, "")

async function gapApiFetch<T>(path: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(`${WORKER_URL}/admin${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(opts?.headers ?? {}) },
  })
  if (!res.ok) {
    const body = await res.text()
    try {
      const json = JSON.parse(body) as { error?: string }
      if (json.error) throw new Error(json.error)
    } catch (e) {
      if (e instanceof Error && e.message !== body) throw e
    }
    throw new Error(body || `API error: ${res.status}`)
  }
  return res.json() as Promise<T>
}

export type GapSyncMode = "conservative" | "broad"
export type GapListKind = "no-url" | "not-crawled"

export interface YutaiGapReport {
  mode: GapSyncMode
  generatedAt: string
  sources: {
    kabunusiyutai: number
    kabuYutai: number
    externalUnion: number
  }
  ours: { published: number }
  missing: string[]
  extra: string[]
  missingNotInDatabase: string[]
  missingWithoutUrl: number
  missingWithUrlNotCrawled: number
  externalBenefitUrlFailed: number
}

export interface GapListItem {
  id: string
  name: string
  officialUrl: string | null
  externalBenefitUrlFailedAt: number | null
  urlSearchFailedAt: number | null
  crawlStatus: string | null
}

export interface GapListPage {
  kind: GapListKind
  mode: GapSyncMode
  items: GapListItem[]
  total: number
  page: number
  limit: number
  totalPages: number
}

const GAP_LIST_PAGE_SIZE = 30

export function useGapSync() {
  const [report, setReport] = useState<YutaiGapReport | null>(null)
  const [loading, setLoading] = useState(false)
  const [listLoading, setListLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [mode, setMode] = useState<GapSyncMode>("broad")
  const [activeListKind, setActiveListKind] = useState<GapListKind | null>(null)
  const [listPage, setListPage] = useState<GapListPage | null>(null)

  // biome-ignore lint/correctness/useExhaustiveDependencies: clear paginated list when comparison mode changes
  useEffect(() => {
    setActiveListKind(null)
    setListPage(null)
  }, [mode])

  const loadReport = useCallback(async (): Promise<YutaiGapReport> => {
    setLoading(true)
    setError(null)
    try {
      const data = await gapApiFetch<YutaiGapReport>(`/gap-report?mode=${mode}`)
      setReport(data)
      return data
    } catch (e) {
      const message = String(e)
      setError(message)
      throw e
    } finally {
      setLoading(false)
    }
  }, [mode])

  async function fetchGapList(
    kind: GapListKind,
    page = 1,
    pageLimit = GAP_LIST_PAGE_SIZE
  ): Promise<GapListPage> {
    setListLoading(true)
    setError(null)
    try {
      const data = await gapApiFetch<GapListPage>(
        `/gap-lists/${kind}?mode=${mode}&page=${page}&limit=${pageLimit}`
      )
      setListPage(data)
      setActiveListKind(kind)
      return data
    } catch (e) {
      const message = String(e)
      setError(message)
      throw e
    } finally {
      setListLoading(false)
    }
  }

  async function toggleGapList(kind: GapListKind): Promise<void> {
    if (activeListKind === kind && listPage) {
      setActiveListKind(null)
      setListPage(null)
      return
    }
    await fetchGapList(kind, 1)
  }

  return {
    report,
    loading,
    listLoading,
    error,
    mode,
    setMode,
    activeListKind,
    listPage,
    loadReport,
    fetchGapList,
    toggleGapList,
    GAP_LIST_PAGE_SIZE,
  }
}
